// kitchen/web/src/views/TableBoard.tsx
// Per-table KDS board view.
// Consumes useOrders + useClientConfig, groups by table, renders TableCard
// in a horizontal scrolling row with ◀ ▶ navigation buttons (US4).

import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useOrders } from "../hooks/useOrders";
import { useClientConfig } from "../hooks/useClientConfig";
import { useKitchenStore } from "../stores/kitchen.store";
import {
  groupOrdersByTable,
  sortTables,
  oldestActiveItemId,
  isActiveKitchenItem,
  filterOrdersByStation,
  stationBoardConfig,
} from "@shared/kds/table-board";
import TableCard from "../components/TableCard";

// ─── Scroll step: one card width (320px) or a sensible fixed amount ───────────
const SCROLL_STEP = 320;

interface TableBoardProps {
  clientId: string;
}

export default function TableBoard({ clientId }: TableBoardProps) {
  const { data: orders, isLoading: ordersLoading } = useOrders(clientId);
  const { config: clientConfig } = useClientConfig(clientId);

  // Single-station monitor (`?station=<id>`, e.g. the bar): only that
  // station's items, with a board config that keeps their categories visible
  // regardless of the client's kitchen mapping.
  const stationFilter = useKitchenStore((s) => s.stationFilter);
  const stations = useKitchenStore((s) => s.stations);
  const scopedOrders = useMemo(
    () =>
      stationFilter ? filterOrdersByStation(orders ?? [], stationFilter) : (orders ?? []),
    [orders, stationFilter],
  );
  const config = useMemo(() => {
    if (!stationFilter) return clientConfig;
    const name = stations.find((s) => s.id === stationFilter)?.name ?? "Stazione";
    return stationBoardConfig(clientConfig, scopedOrders, name);
  }, [clientConfig, stationFilter, stations, scopedOrders]);

  // US3 + timer mm:ss: ticka ogni secondo così i timer per-piatto scorrono
  // in tempo reale (e i colori ritardo si aggiornano di conseguenza).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  // US4: ref to the scrollable container for programmatic scroll + visibility.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Recompute scroll button visibility.
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Se c'è overflow orizzontale mostra SEMPRE entrambe le frecce, così lo
    // chef può sempre tornare indietro anche dopo aver scrollato a destra.
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    setCanScrollLeft(hasOverflow);
    setCanScrollRight(hasOverflow);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });

    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  // Also recompute after data changes (number of cards may change).
  const tableGroupsLength = useMemo(() => {
    return (orders ?? []).length;
  }, [orders]);

  useEffect(() => {
    // Allow one frame for the DOM to update before measuring.
    const raf = requestAnimationFrame(updateScrollState);
    return () => cancelAnimationFrame(raf);
  }, [tableGroupsLength, updateScrollState]);

  // Filter out COMPLETED and CANCELLED orders (same as ChefOverview station view).
  const activeOrders = useMemo(
    () =>
      scopedOrders.filter(
        (o: any) => o.status !== "COMPLETED" && o.status !== "CANCELLED",
      ),
    [scopedOrders],
  );

  // Group into table groups.
  const tableGroups = useMemo(
    () => groupOrdersByTable(activeOrders, config),
    [activeOrders, config],
  );

  // FR-011: scarta i gruppi senza piatti CUCINA attivi (categoria mappata).
  // Un tavolo con sole bevande non mappate non compare nella vista cuoco.
  const activeGroups = useMemo(
    () => tableGroups.filter((g) => g.items.some((i) => isActiveKitchenItem(i, config))),
    [tableGroups, config],
  );

  // US3 (FR-013): sort by wait descending, respecting takeawayPosition config.
  const sortedGroups = useMemo(
    () => sortTables(activeGroups, config, now),
    [activeGroups, config, now],
  );

  // FR-015: single oldest active item id across all visible groups.
  const oldestId = useMemo(
    () => oldestActiveItemId(sortedGroups, now, config),
    [sortedGroups, now, config],
  );

  if (ordersLoading) {
    return (
      <div className="flex items-center justify-center flex-1 text-kds-muted">
        <span className="w-6 h-6 border-2 border-kds-muted border-t-kds-text rounded-full animate-spin mr-3" />
        Caricamento ordini…
      </div>
    );
  }

  if (sortedGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 text-kds-muted gap-2">
        <p className="text-xl">Nessun tavolo in servizio</p>
        <p className="text-sm">I tavoli aperti appariranno qui automaticamente</p>
      </div>
    );
  }

  // Frecce SEMPRE montate: attive (rosse, pulsanti) quando c'è overflow,
  // spente (grigie, non cliccabili) altrimenti. Lo chef non le vede mai
  // sparire del tutto → niente confusione/blocchi.
  const activeBtn =
    "bg-red-600 text-white border-2 border-red-300 shadow-xl animate-pulse active:scale-95";
  const disabledBtn =
    "bg-kds-card/60 text-kds-muted border-2 border-kds-muted/20 cursor-default";

  return (
    // Outer wrapper: relative for absolute-positioned scroll buttons (US4).
    // min-h-0 CRITICO: senza, il flex item cresce col contenuto (min-height:auto
    // di default) e le card escono dallo schermo invece di scrollare.
    <div className="flex-1 min-h-0 relative overflow-hidden flex">
      {/* US4 ◀ left scroll button — sempre visibile */}
      <button
        type="button"
        aria-label="Scorri a sinistra"
        disabled={!canScrollLeft}
        onClick={() => {
          scrollRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: "smooth" });
        }}
        className={[
          "absolute left-0 top-1/2 -translate-y-1/2 z-20",
          "flex items-center justify-center",
          "w-12 h-24 rounded-r-xl",
          canScrollLeft ? activeBtn : disabledBtn,
        ].join(" ")}
      >
        <ChevronLeft size={36} strokeWidth={3} />
      </button>

      {/* Horizontal scrolling row of TableCards (FR-017/FR-019). */}
      <div
        ref={scrollRef}
        className="flex-1 min-w-0 h-full overflow-x-auto overflow-y-hidden"
        style={{ scrollbarWidth: "thin" }}
      >
        <div
          className="flex gap-4 h-full px-1 py-1"
          style={{ minWidth: "max-content" }}
        >
          {sortedGroups.map((group) => (
            <div key={group.key} className="flex-shrink-0 h-full overflow-hidden">
              <TableCard
                group={group}
                config={config}
                oldestItemId={oldestId}
                now={now}
                flat={!!stationFilter}
              />
            </div>
          ))}
        </div>
      </div>

      {/* US4 ▶ right scroll button — sempre visibile */}
      <button
        type="button"
        aria-label="Scorri a destra"
        disabled={!canScrollRight}
        onClick={() => {
          scrollRef.current?.scrollBy({ left: SCROLL_STEP, behavior: "smooth" });
        }}
        className={[
          "absolute right-0 top-1/2 -translate-y-1/2 z-20",
          "flex items-center justify-center",
          "w-12 h-24 rounded-l-xl",
          canScrollRight ? activeBtn : disabledBtn,
        ].join(" ")}
      >
        <ChevronRight size={36} strokeWidth={3} />
      </button>
    </div>
  );
}
